/**
 * VirtualJoystick - dynamic mobile touch joystick control
 */

export class VirtualJoystick {
    constructor(options = {}) {
        this.parent = options.parent || document.getElementById('gameContainer') || document.body;
        this.onMove = options.onMove || null;
        this.maxDistance = options.maxDistance || 60;
        this.baseSize = options.baseSize || 140;
        this.knobSize = options.knobSize || 70;

        this.activePointerId = null;
        this.isActive = false;
        this.isEnabled = false;
        this.position = { x: 0, y: 0 };
        this.vector = { x: 0, y: 0 };

        this.root = null;
        this.base = null;
        this.knob = null;

        this._boundPointerDown = this.handlePointerDown.bind(this);
        this._boundPointerMove = this.handlePointerMove.bind(this);
        this._boundPointerUp = this.handlePointerUp.bind(this);
    }

    init() {
        if (this.root || !this.parent) return;

        this.root = document.createElement('div');
        this.root.id = 'virtualJoystick';
        this.root.style.position = 'absolute';
        this.root.style.left = '0px';
        this.root.style.top = '0px';
        this.root.style.width = `${this.baseSize}px`;
        this.root.style.height = `${this.baseSize}px`;
        this.root.style.pointerEvents = 'none';
        this.root.style.display = 'none';
        this.root.style.opacity = '0';
        this.root.style.zIndex = '250';

        this.base = document.createElement('div');
        this.base.style.position = 'relative';
        this.base.style.width = '100%';
        this.base.style.height = '100%';
        this.base.style.borderRadius = '50%';
        this.base.style.background = 'rgba(255, 255, 255, 0.015)';
        this.base.style.border = '2px solid rgba(180, 235, 255, 0.2)';
        this.base.style.boxShadow = '0 0 4px rgba(180, 235, 255, 0.12) inset';
        this.base.style.pointerEvents = 'none';
        this.base.style.touchAction = 'none';

        this.knob = document.createElement('div');
        this.knob.style.position = 'absolute';
        this.knob.style.left = '50%';
        this.knob.style.top = '50%';
        this.knob.style.width = `${this.knobSize}px`;
        this.knob.style.height = `${this.knobSize}px`;
        this.knob.style.marginLeft = `${-this.knobSize / 2}px`;
        this.knob.style.marginTop = `${-this.knobSize / 2}px`;
        this.knob.style.borderRadius = '50%';
        this.knob.style.background = 'rgba(215, 245, 255, 0.12)';
        this.knob.style.border = '2px solid rgba(215, 245, 255, 0.3)';
        this.knob.style.boxShadow = '0 0 3px rgba(215, 245, 255, 0.2)';

        this.base.appendChild(this.knob);
        this.root.appendChild(this.base);
        this.parent.appendChild(this.root);
        this.parent.addEventListener('pointerdown', this._boundPointerDown, { passive: false });
    }

    setMoveHandler(handler) {
        this.onMove = handler;
    }

    show() {
        this.init();
        this.isEnabled = true;
        if (this.root) {
            this.root.style.display = 'block';
            this.root.style.opacity = '0';
        }
    }

    hide() {
        this.isEnabled = false;
        this.reset();
        if (this.root) {
            this.root.style.display = 'none';
        }
    }

    reset() {
        this.isActive = false;
        this.activePointerId = null;
        this.vector = { x: 0, y: 0 };
        window.removeEventListener('pointermove', this._boundPointerMove);
        window.removeEventListener('pointerup', this._boundPointerUp);
        window.removeEventListener('pointercancel', this._boundPointerUp);
        if (this.knob) {
            this.knob.style.transform = 'translate3d(0, 0, 0)';
        }
        if (this.root) {
            this.root.style.opacity = this.isEnabled ? '0' : '0';
        }
        this.emitMove(false);
    }

    emitMove(active) {
        if (typeof this.onMove === 'function') {
            this.onMove({
                x: this.vector.x,
                y: this.vector.y,
                active
            });
        }
    }

    handlePointerDown(event) {
        if (!this.isEnabled || this.isActive) return;
        if (event.pointerType !== 'touch' && event.pointerType !== 'pen' && event.pointerType !== 'mouse') return;
        if (event.target && typeof event.target.closest === 'function' && event.target.closest('[data-ui-interactive]')) return;

        this.isActive = true;
        this.activePointerId = event.pointerId;

        const parentRect = this.parent.getBoundingClientRect();
        const x = event.clientX - parentRect.left;
        const y = event.clientY - parentRect.top;
        const half = this.baseSize / 2;
        const maxLeft = parentRect.width - this.baseSize;
        const maxTop = parentRect.height - this.baseSize;
        const left = Math.max(0, Math.min(maxLeft, x - half));
        const top = Math.max(0, Math.min(maxTop, y - half));

        this.root.style.left = `${left}px`;
        this.root.style.top = `${top}px`;
        this.root.style.display = 'block';
        this.root.style.opacity = '0.45';

        if (this.knob) {
            this.knob.style.transform = 'translate3d(0, 0, 0)';
        }

        this.updateVectorFromEvent(event);
        this.emitMove(true);

        window.addEventListener('pointermove', this._boundPointerMove, { passive: false });
        window.addEventListener('pointerup', this._boundPointerUp, { passive: false });
        window.addEventListener('pointercancel', this._boundPointerUp, { passive: false });

        if (event.cancelable) {
            event.preventDefault();
        }
    }

    handlePointerMove(event) {
        if (!this.isActive || event.pointerId !== this.activePointerId) return;
        this.updateVectorFromEvent(event);
        this.emitMove(true);
        if (event.cancelable) {
            event.preventDefault();
        }
    }

    handlePointerUp(event) {
        if (!this.isActive || event.pointerId !== this.activePointerId) return;
        this.reset();
        if (event.cancelable) {
            event.preventDefault();
        }
    }

    updateVectorFromEvent(event) {
        if (!this.base) return;

        const rect = this.base.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const dx = event.clientX - centerX;
        const dy = event.clientY - centerY;

        const distance = Math.sqrt(dx * dx + dy * dy);
        const maxDist = this.maxDistance;
        const clampedDistance = Math.min(distance, maxDist);
        const angle = Math.atan2(dy, dx);

        const offsetX = Math.cos(angle) * clampedDistance;
        const offsetY = Math.sin(angle) * clampedDistance;
        if (this.knob) {
            this.knob.style.transform = `translate3d(${offsetX}px, ${offsetY}px, 0)`;
        }

        this.vector.x = offsetX / maxDist;
        this.vector.y = offsetY / maxDist;
    }

    dispose() {
        this.reset();
        if (this.parent) {
            this.parent.removeEventListener('pointerdown', this._boundPointerDown);
        }
        if (this.root && this.root.parentNode) {
            this.root.parentNode.removeChild(this.root);
        }
        this.root = null;
        this.base = null;
        this.knob = null;
        this.onMove = null;
    }
}
